import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { API, axios } from "@/api";
import {
  ArrowLeft, TrendingUp, Users, Building2, ShieldCheck, Bot,
  BarChart3, MapPin, Database, ChevronDown, ChevronRight,
  Loader2, Zap, Target, Sparkles, Wand2, Palette, Eye, Radio,
  FileWarning, CheckCircle, XCircle, Mail, DollarSign, AlertTriangle, Bell, GitMerge,
  Activity, SlidersHorizontal, Grid3X3, Repeat, Compass, ShieldAlert, HeartPulse, Bug, Shield,
  Flame, Award, Globe, Gauge, LayoutDashboard, Wrench, BarChart2, Star,
  Menu, X, Check, BookOpen, Beaker, Share2, MessageSquare, Layers, Search, Link2, Camera, Calendar, Lightbulb,
} from "lucide-react";
import OfferIntelPanel from "@/components/admin/OfferIntelPanel";
import SearchAnalyticsPanel from "@/components/admin/SearchAnalyticsPanel";
import PlaybooksPanel from "@/components/admin/PlaybooksPanel";
import GeoDrilldownPanel from "@/components/admin/GeoDrilldownPanel";
import RateLimitPanel from "@/components/admin/RateLimitPanel";
import LiveCityNightlyTile from "@/components/admin/LiveCityNightlyTile";
import AlertsPanel from "@/components/admin/AlertsPanel";
import PanelRefreshButton from "@/components/admin/PanelRefreshButton";
import ConversionFunnelPanel from "@/components/admin/ConversionFunnelPanel";
import LoginActivityPanel from "@/components/admin/LoginActivityPanel";
import FilterAnalyticsPanel from "@/components/admin/FilterAnalyticsPanel";
import CategoryDemandPanel from "@/components/admin/CategoryDemandPanel";
import OfferStickinessPanel from "@/components/admin/OfferStickinessPanel";
import DiscoveryAnalyticsPanel from "@/components/admin/DiscoveryAnalyticsPanel";
import SessionInsightsPanel from "@/components/admin/SessionInsightsPanel";
import DuplicateCheckPanel from "@/components/admin/DuplicateCheckPanel";
import RetentionDashboardPanel from "@/components/admin/RetentionDashboardPanel";
import BugBountyPanel from "@/components/admin/BugBountyPanel";
import DealVelocityPanel from "@/components/admin/DealVelocityPanel";
import OfferPerformancePanel from "@/components/admin/OfferPerformancePanel";
import GeographicDemandPanel from "@/components/admin/GeographicDemandPanel";
import FrictionIndexPanel from "@/components/admin/FrictionIndexPanel";
import ActivityFeedPanel from "@/components/admin/ActivityFeedPanel";
import DataCompletenessPanel from "@/components/admin/DataCompletenessPanel";
import WorkflowPipelinePanel from "@/components/admin/WorkflowPipelinePanel";
import BetaTestersPanel from "@/components/admin/BetaTestersPanel";
import MonthlyChallengePanel from "@/components/admin/MonthlyChallengePanel";
import PhotoReviewPanel from "@/components/admin/PhotoReviewPanel";
import VendorOutreachPanel from "@/components/admin/VendorOutreachPanel";
import MelissaDripPanel from "@/components/admin/MelissaDripPanel";
import PushNotificationsPanel from "@/components/admin/PushNotificationsPanel";
import SocialMediaPanel from "@/components/admin/SocialMediaPanel";
import SystemHealthPanel from "@/components/admin/SystemHealthPanel";
import DecisionIntelligenceGroup from "@/components/admin/DecisionIntelligenceGroup";
import DecisionIntelligenceSummaryTile from "@/components/admin/DecisionIntelligenceSummaryTile";
import DecisionIssueDrawer from "@/components/admin/DecisionIssueDrawer";
import CopyBankPanel from "@/components/admin/CopyBankPanel";
import PendingDeletionsPanel from "@/components/admin/PendingDeletionsPanel";
import CityQueuePanel from "@/components/admin/CityQueuePanel";
import StagingQueuePanel from "@/components/admin/StagingQueuePanel";
import SweepPerformancePanel from "@/components/admin/SweepPerformancePanel";
import PoolHealthScorecardPanel from "@/components/admin/PoolHealthScorecardPanel";
import InboxPanel from "@/components/admin/InboxPanel";
import QuarantineQueuePanel from "@/components/admin/QuarantineQueuePanel";
import SubmissionsPanel from "@/components/admin/SubmissionsPanel";
import CityClassificationPanel from "@/components/admin/CityClassificationPanel";
import PaidSweepQueuePanel from "@/components/admin/PaidSweepQueuePanel";
import IntentSignalsPanel from "@/components/admin/IntentSignalsPanel";
import ChainConsistencyPanel from "@/components/admin/ChainConsistencyPanel";
import OfferIntelligencePanel from "@/components/admin/OfferIntelligencePanel";
import BatchBudgetPanel from "@/components/admin/BatchBudgetPanel";
import TonightsSpendPanel from "@/components/admin/TonightsSpendPanel";
import CostTrackerPanel from "@/components/admin/CostTrackerPanel";
import FoundationHealthPanel from "@/components/admin/FoundationHealthPanel";
import VendorManagementPanel from "@/components/admin/VendorManagementPanel";
import VendorAnalyticsPanel from "@/components/admin/VendorAnalyticsPanel";
import B2BDashboardPanel from "@/components/admin/B2BDashboardPanel";
import BrandContributionsPanel from "@/components/admin/BrandContributionsPanel";
import DietarySuggestionsPanel from "@/components/admin/DietarySuggestionsPanel";
import BrandTapAnalyticsPanel from "@/components/admin/BrandTapAnalyticsPanel";
import PredictHQPanel from "@/components/admin/PredictHQPanel";
import FoodHolidayPanel from "@/components/admin/FoodHolidayPanel";
import TipsManagementPanel from "@/components/admin/TipsManagementPanel";
import PortfolioVendorsPanel from "@/components/admin/PortfolioVendorsPanel";
import AgeRestrictionsPanel from "@/components/admin/AgeRestrictionsPanel";
import CategoryIntelligencePanel from "@/components/admin/CategoryIntelligencePanel";
import OutreachInfrastructurePanel from "@/components/admin/OutreachInfrastructurePanel";
import RollingPipelineStatusPanel from "@/components/admin/RollingPipelineStatusPanel";
import CoverageMapPanel from "@/components/admin/CoverageMapPanel";
import SourceROIPanel from "@/components/admin/SourceROIPanel";
import CityManagementPanel from "@/components/admin/CityManagementPanel";
import AdminOffersPanel from "@/components/admin/AdminOffersPanel";
import LegendCard from "@/components/admin/LegendCard";
import NorthStarPanel from "@/components/admin/NorthStarPanel";
import SourceHealthPanel from "@/components/admin/SourceHealthPanel";
import { PipelinesMergedPanel, AcquisitionMergedPanel, DataQualityMergedPanel, AddressVerificationMergedPanel } from "@/components/admin/MergedPanels";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { toast } from "sonner";

// ── Status Dot ──────────────────────────────────────────────────
const StatusDot = ({ status, size = "sm" }) => {
  const colors = {
    red: "bg-red-500", yellow: "bg-amber-400", green: "bg-emerald-400", blue: "bg-sky-400",
  };
  const s = size === "sm" ? "w-2 h-2" : "w-2.5 h-2.5";
  return (
    <span className={`${s} rounded-full inline-block flex-shrink-0 ${colors[status] || colors.green} ${status === "red" ? "animate-pulse" : ""}`} />
  );
};

// ── Sparkline SVG ───────────────────────────────────────────────
const Sparkline = ({ data, color = "#64748b", width = 48, height = 16 }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 2) - 1;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={width} height={height} className="inline-block" viewBox={`0 0 ${width} ${height}`}>
      <polyline fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={points} />
    </svg>
  );
};

// ── Trend Arrow ─────────────────────────────────────────────────
const TrendArrow = ({ trend, change, inverted = false }) => {
  if (!trend || trend === "flat") {
    return <span className="text-[9px] text-slate-400 font-bold">—</span>;
  }
  // For "bad" metrics (alerts, friction), going up is bad. inverted flips the color.
  const isGood = inverted ? trend === "down" : trend === "up";
  const isBad = inverted ? trend === "up" : trend === "down";
  const color = isGood ? "text-emerald-500" : isBad ? "text-red-500" : "text-slate-400";
  const arrow = trend === "up" ? "▲" : "▼";
  const abs = Math.abs(change || 0);
  return (
    <span className={`text-[9px] font-bold ${color} flex items-center gap-0.5`}>
      {arrow} {abs > 0 ? abs : ""}
    </span>
  );
};

// ── Stat Card (color-coded) ─────────────────────────────────────
const StatCard = ({ label, value, sub, color = "text-slate-800", size = "md", performance, sparkline, trend, trendChange, invertTrend }) => {
  // performance: "good" | "warn" | "bad" | undefined
  const perfBorder = {
    good: "border-emerald-200 bg-emerald-50/40",
    warn: "border-amber-200 bg-amber-50/40",
    bad: "border-red-200 bg-red-50/40",
  };
  const perfDot = { good: "bg-emerald-400", warn: "bg-amber-400", bad: "bg-red-500 animate-pulse" };
  const perfStyle = performance ? perfBorder[performance] || "" : "";

  return (
    <div className={`rounded-xl border shadow-sm ${size === "lg" ? "p-5" : "p-3"} ${perfStyle || "bg-white border-slate-100"}`} data-testid={`stat-${label?.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          {performance && <span className={`w-1.5 h-1.5 rounded-full ${perfDot[performance]}`} />}
          <p className="text-[11px] text-slate-500 font-medium truncate">{label}</p>
        </div>
        {trend && <TrendArrow trend={trend} change={trendChange} inverted={invertTrend} />}
      </div>
      <div className="flex items-end justify-between gap-2">
        <p className={`${size === "lg" ? "text-3xl" : "text-xl"} font-bold ${color} leading-none`}>{value}</p>
        {sparkline && <Sparkline data={sparkline} color={performance === "bad" ? "#ef4444" : performance === "good" ? "#10b981" : "#94a3b8"} />}
      </div>
      {sub && <p className="text-[10px] text-slate-400 mt-1">{sub}</p>}
    </div>
  );
};

// ── Section Panel (used inside tabs) ────────────────────────────
const ReferralIntelPanel = ({ data }) => {
  if (!data) return <p className="text-xs text-slate-400 text-center py-4">Loading referral data...</p>;
  const f = data.conversion_funnel || {};
  return (
    <div className="space-y-4" data-testid="referral-intel-panel">
      <div className="grid grid-cols-3 gap-2">
        <StatCard label="Links Generated" value={data.total_generated || 0} color="text-slate-800" />
        <StatCard label="Installs" value={data.total_installed || 0} color="text-sky-600" />
        <StatCard label="Rewarded" value={data.total_rewarded || 0} color="text-green-600" />
      </div>
      <div className="grid grid-cols-4 gap-2">
        <StatCard label="Click Rate" value={`${f.click_rate || 0}%`} color="text-blue-600" />
        <StatCard label="Install Rate" value={`${f.install_rate || 0}%`} color="text-sky-600" />
        <StatCard label="Qualify Rate" value={`${f.qualify_rate || 0}%`} color="text-amber-600" />
        <StatCard label="Reward Rate" value={`${f.reward_rate || 0}%`} color="text-green-600" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <StatCard label="Qualifying" value={data.total_qualifying || 0} color="text-amber-600" />
        <StatCard label="Expired" value={data.total_expired || 0} color="text-red-500" />
        <StatCard label="Stale" value={data.total_stale || 0} color="text-slate-400" />
      </div>
      <StatCard label="Total Points Awarded" value={data.total_points_awarded || 0} color="text-amber-500" size="lg" />
      {(data.top_referrers_by_sent || []).length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">Top Referrers (by Sent)</p>
          {data.top_referrers_by_sent.map((r, i) => (
            <div key={i} className="flex items-center justify-between py-1 text-xs">
              <span className="text-slate-600">{r.name || r.email}</span>
              <span className="font-bold text-slate-800">{r.count}</span>
            </div>
          ))}
        </div>
      )}
      {(data.top_referrers_by_qualified || []).length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">Top Referrers (by Qualified)</p>
          {data.top_referrers_by_qualified.map((r, i) => (
            <div key={i} className="flex items-center justify-between py-1 text-xs">
              <span className="text-slate-600">{r.name || r.email}</span>
              <span className="font-bold text-green-600">{r.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SectionPanel = ({ id, title, icon: Icon, iconColor, children, defaultOpen = false, onOpen, panelStatus }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div id={id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden scroll-mt-24">
      <button onClick={() => { const next = !open; setOpen(next); if (next && onOpen) onOpen(); }} className="w-full px-5 py-4 flex items-center gap-3 hover:bg-slate-50 transition-colors" data-testid={`section-${title.toLowerCase().replace(/[\s()]+/g, '-')}`}>
        <div className={`w-9 h-9 rounded-xl ${iconColor} flex items-center justify-center flex-shrink-0`}><Icon className="w-4.5 h-4.5" /></div>
        <span className="text-sm font-semibold text-slate-800 flex-1 text-left">{title}</span>
        {panelStatus && panelStatus !== "green" && <StatusDot status={panelStatus} />}
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
            <div className="px-5 pb-5 pt-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Panel Definitions ───────────────────────────────────────────
const PANEL_DEFS = {
  cockpit: [
    { id: "pipeline", title: "Workflow Pipeline", icon: Activity, color: "bg-sky-100 text-sky-600" },
    { id: "dev-tools", title: "Dev Tools", icon: Palette, color: "bg-purple-100 text-purple-600" },
    { id: "friction", title: "Offer Friction Index", icon: Gauge, color: "bg-red-100 text-red-600" },
    { id: "alerts", title: "Admin Alerts", icon: Bell, color: "bg-red-100 text-red-600" },
    { id: "source-health", title: "Source Health", icon: Activity, color: "bg-emerald-100 text-emerald-600" },
    { id: "price-reports", title: "Price Reports", icon: FileWarning, color: "bg-red-100 text-red-600" },
    { id: "data-completeness", title: "Data Completeness (S15)", icon: Database, color: "bg-teal-100 text-teal-600" },
    { id: "reverification", title: "Reverification Priority (S20)", icon: Target, color: "bg-orange-100 text-orange-600" },
    { id: "beta-testers", title: "Beta Testers", icon: Beaker, color: "bg-purple-100 text-purple-600" },
    { id: "workspace-inbox", title: "Workspace Inbox", icon: Mail, color: "bg-sky-100 text-sky-600" },
    { id: "monthly-challenge", title: "Monthly Challenge", icon: Award, color: "bg-indigo-100 text-indigo-600" },
    { id: "melissa-drip", title: "Address Verification", icon: MapPin, color: "bg-teal-100 text-teal-600" },
    { id: "push-notifications", title: "Push Notifications", icon: Bell, color: "bg-amber-100 text-amber-600" },
    { id: "social-media", title: "Social Media Links", icon: Share2, color: "bg-indigo-100 text-indigo-600" },
    { id: "copy-bank", title: "Copy Bank", icon: MessageSquare, color: "bg-cyan-100 text-cyan-600" },
    { id: "pending-deletions", title: "Pending Deletions", icon: Shield, color: "bg-red-100 text-red-600" },
    { id: "staging-queue", title: "Staging Queue", icon: Layers, color: "bg-amber-100 text-amber-600" },
    { id: "sweep-performance", title: "Sweep Performance", icon: TrendingUp, color: "bg-blue-100 text-blue-600" },
    { id: "pool-health", title: "Pool Health Scorecard", icon: TrendingUp, color: "bg-emerald-100 text-emerald-600" },
    { id: "quarantine-queue", title: "Quarantine Queue", icon: Shield, color: "bg-orange-100 text-orange-600" },
    { id: "submissions-queue", title: "User Submissions", icon: MessageSquare, color: "bg-teal-100 text-teal-600" },
    { id: "city-classification", title: "City Classifications", icon: MapPin, color: "bg-violet-100 text-violet-600" },
    { id: "paid-sweep-queue", title: "Paid Sweep Queue", icon: DollarSign, color: "bg-emerald-100 text-emerald-600" },
    { id: "intent-signals", title: "Intent Signals", icon: Search, color: "bg-sky-100 text-sky-600" },
    { id: "chain-consistency", title: "Chain Consistency", icon: Link2, color: "bg-rose-100 text-rose-600" },
    { id: "offer-intelligence", title: "Offer Intelligence", icon: TrendingUp, color: "bg-indigo-100 text-indigo-600" },
    { id: "batch-budget", title: "Batch Coordinator & Budget", icon: Layers, color: "bg-cyan-100 text-cyan-600" },
    { id: "tonights-spend", title: "Tonight's Spend", icon: TrendingUp, color: "bg-emerald-100 text-emerald-700" },
    { id: "cost-tracker", title: "Live Cost Tracker", icon: TrendingUp, color: "bg-emerald-100 text-emerald-700" },
    { id: "foundation-health", title: "Foundation Health", icon: TrendingUp, color: "bg-indigo-100 text-indigo-700" },
    { id: "vendor-management", title: "Vendor Verification", icon: Shield, color: "bg-violet-100 text-violet-600" },
    { id: "vendor-analytics", title: "Vendor Analytics", icon: TrendingUp, color: "bg-amber-100 text-amber-600" },
    { id: "portfolio-vendors", title: "Portfolio Vendors", icon: Users, color: "bg-purple-100 text-purple-600" },
    { id: "photo-review", title: "Photo Review Queue", icon: Camera, color: "bg-orange-100 text-orange-600" },
    { id: "vendor-outreach", title: "Vendor Outreach", icon: Mail, color: "bg-green-100 text-green-600" },
    { id: "b2b-dashboard", title: "B2B Platform Dashboard", icon: Building2, color: "bg-indigo-100 text-indigo-600" },
    { id: "brand-contributions", title: "Brand Contributions", icon: BookOpen, color: "bg-amber-100 text-amber-600" },
    { id: "brand-tap-analytics", title: "Brand Tap Analytics", icon: BarChart3, color: "bg-orange-100 text-orange-600" },
    { id: "dietary-suggestions", title: "Dietary Suggestions", icon: Beaker, color: "bg-emerald-100 text-emerald-600" },
    { id: "predictiq-events", title: "PredictHQ Events", icon: Compass, color: "bg-blue-100 text-blue-600" },
    { id: "food-holidays", title: "Food Holidays", icon: Calendar, color: "bg-amber-100 text-amber-600" },
    { id: "tips-management", title: "Tips Management", icon: Lightbulb, color: "bg-amber-100 text-amber-600" },
    { id: "age-restrictions", title: "Age Restrictions", icon: Shield, color: "bg-red-100 text-red-600" },
    { id: "category-intelligence", title: "Category Intelligence", icon: Sparkles, color: "bg-teal-100 text-teal-600" },
    { id: "outreach-infrastructure", title: "Outreach Infrastructure", icon: Radio, color: "bg-indigo-100 text-indigo-600" },
    { id: "rolling-pipeline-status", title: "Rolling Pipeline Status", icon: Activity, color: "bg-sky-100 text-sky-700" },
    { id: "coverage-map", title: "Coverage Map", icon: Globe, color: "bg-teal-100 text-teal-600" },
    { id: "source-roi", title: "Source ROI Dashboard", icon: TrendingUp, color: "bg-emerald-100 text-emerald-600" },
    { id: "city-management", title: "City Management", icon: MapPin, color: "bg-violet-100 text-violet-600" },
    { id: "admin-offers", title: "Offers Management", icon: Star, color: "bg-amber-100 text-amber-600" },
    { id: "referral-intel", title: "Referral Intelligence", icon: Users, color: "bg-pink-100 text-pink-600" },
  ],
  ops: [
    { id: "marketplace", title: "Marketplace Health", icon: Target, color: "bg-amber-100 text-amber-600" },
    { id: "geographic", title: "Geographic Demand", icon: Globe, color: "bg-teal-100 text-teal-600" },
    { id: "performance", title: "Offer Performance Ranking", icon: Award, color: "bg-amber-100 text-amber-600" },
    { id: "velocity", title: "Deal Velocity", icon: Flame, color: "bg-red-100 text-red-600" },
    { id: "funnel", title: "Conversion Funnel", icon: GitMerge, color: "bg-violet-100 text-violet-600" },
    { id: "retention", title: "Retention & Churn", icon: HeartPulse, color: "bg-emerald-100 text-emerald-600" },
    { id: "category-demand", title: "Category Demand", icon: Grid3X3, color: "bg-indigo-100 text-indigo-600" },
    { id: "user-growth", title: "User Growth", icon: Users, color: "bg-sky-100 text-sky-600" },
    { id: "supply", title: "Supply Health", icon: Building2, color: "bg-emerald-100 text-emerald-600" },
    { id: "demand", title: "Demand Activity", icon: TrendingUp, color: "bg-orange-100 text-orange-600" },
    { id: "redemptions", title: "Redemption Intelligence", icon: Zap, color: "bg-amber-100 text-amber-600" },
    { id: "category-perf", title: "Category Performance", icon: BarChart3, color: "bg-indigo-100 text-indigo-600" },
  ],
  tools: [
    { id: "filter-usage", title: "Filter Usage", icon: SlidersHorizontal, color: "bg-teal-100 text-teal-600" },
    { id: "login-activity", title: "Login Activity (DAU)", icon: Activity, color: "bg-sky-100 text-sky-600" },
    { id: "stickiness", title: "Offer Stickiness", icon: Repeat, color: "bg-violet-100 text-violet-600" },
    { id: "bug-bounty", title: "Bug Bounty Reports", icon: Bug, color: "bg-red-100 text-red-600" },
    { id: "duplicates", title: "Duplicate Check", icon: ShieldAlert, color: "bg-orange-100 text-orange-600" },
    { id: "discovery", title: "Discovery Channels", icon: Compass, color: "bg-indigo-100 text-indigo-600" },
    { id: "sessions", title: "Session Insights", icon: Zap, color: "bg-amber-100 text-amber-600" },
    { id: "offer-intel", title: "Offer Intelligence", icon: Eye, color: "bg-cyan-100 text-cyan-600" },
    { id: "search", title: "Search Analytics", icon: Target, color: "bg-violet-100 text-violet-600" },
    { id: "verification", title: "Verification & Trust", icon: ShieldCheck, color: "bg-green-100 text-green-600" },
    { id: "ai-discovery", title: "A.I. Discovery", icon: Bot, color: "bg-purple-100 text-purple-600" },
    { id: "geo-mpm", title: "Geographic Coverage (MPM)", icon: MapPin, color: "bg-teal-100 text-teal-600" },
    { id: "freshness", title: "Data Freshness", icon: Database, color: "bg-slate-200 text-slate-600" },
    { id: "playbooks", title: "Research Playbooks", icon: BookOpen, color: "bg-violet-100 text-violet-600" },
    { id: "geo-drilldown", title: "Census Geo Hierarchy", icon: Globe, color: "bg-teal-100 text-teal-600" },
    { id: "rate-limits", title: "API Rate Limits", icon: Gauge, color: "bg-orange-100 text-orange-600" },
  ],
};

const TAB_META = {
  cockpit: { label: "Cockpit", icon: LayoutDashboard, desc: "Daily action items" },
  ops: { label: "Ops", icon: BarChart2, desc: "Weekly deep-dives" },
  tools: { label: "Tools", icon: Wrench, desc: "Utilities & diagnostics" },
};

// ═══════════════════════════════════════════════════════════════
// v1.0.0 Batch B — 12 Group Reorganization
// Replaces the 3-tab (Cockpit/Ops/Tools) layout with 12 collapsible groups.
// Priority order top → bottom. Panels listed alphabetically within each group
// via sortPanelsAlpha() — red/orange dots float to the top.
// Merged panels (Pipelines, Acquisition, Data Quality, Address Verification)
// render a single combined panel via MergedPanels wrappers.
// 4 panels (b2b-dashboard, brand-tap-analytics, dev-tools, monetization) are
// gated via new feature flags and silently hidden when flag=false.
// ═══════════════════════════════════════════════════════════════
const GROUPS = [
  { id: "health_alerts", label: "Health & Alerts",
    panels: ["alerts", "source-health", "price-reports", "bug-bounty", "friction"] },
  { id: "pipeline_queues", label: "Pipeline & Queues",
    panels: ["pipeline-merged", "tonights-spend", "cost-tracker", "foundation-health", "paid-sweep-queue", "staging-queue", "quarantine-queue", "batch-budget", "sweep-performance", "pool-health"] },
  { id: "inbound_moderation", label: "Inbound Moderation",
    panels: ["submissions-queue", "photo-review", "pending-deletions", "intent-signals"] },
  { id: "data_quality", label: "Data Quality",
    panels: ["data-quality-merged", "chain-consistency", "duplicates", "freshness", "address-verification-merged"] },
  { id: "growth_analytics", label: "Growth & Analytics",
    panels: ["user-growth", "retention", "funnel", "stickiness", "sessions", "login-activity",
             "marketplace", "demand", "supply", "redemptions", "performance", "velocity",
             "category-demand", "category-perf", "search", "filter-usage"] },
  { id: "acquisition", label: "Acquisition",
    panels: ["acquisition-merged", "referral-intel"] },
  { id: "coverage_discovery", label: "Coverage & Discovery",
    panels: ["coverage-map", "geo-mpm", "geo-drilldown", "geographic", "city-management",
             "city-classification", "ai-discovery", "offer-intel", "offer-intelligence"] },
  { id: "vendors", label: "Vendors",
    panels: ["vendor-management", "vendor-analytics", "portfolio-vendors", "vendor-outreach", "b2b-dashboard"] },
  { id: "content_catalog", label: "Content & Catalog",
    panels: ["admin-offers", "copy-bank", "tips-management", "category-intelligence",
             "brand-contributions", "brand-tap-analytics", "dietary-suggestions",
             "age-restrictions", "predictiq-events", "food-holidays"] },
  { id: "integration_ingest", label: "Integration & Ingest",
    panels: ["outreach-infrastructure", "verification", "playbooks", "rate-limits",
             "push-notifications", "social-media"] },
  { id: "admin_tools", label: "Admin Tools",
    panels: ["beta-testers", "monthly-challenge", "dev-tools", "workspace-inbox"] },
  // v1.0.8 Document 4 — Decision Intelligence is group 13.
  // Sits between Admin Tools and the System & Flags Danger Zone.
  // panels=[] because the four sub-panels are rendered by
  // DecisionIntelligenceGroup.jsx directly (special-cased in the renderer
  // below — same pattern as the digest-preview action row injected into
  // pipeline_queues).
  { id: "decision_intelligence", label: "Decision Intelligence",
    panels: [] },
  { id: "system_flags", label: "System & Flags", dangerZone: true,
    panels: [] }, // danger zone — feature flags UI is already surfaced via /admin/feature-flags endpoints
];

// Feature-flag gating for admin panels (v1.0.0 Batch B Item 13).
const PANEL_FLAG_GATES = {
  "b2b-dashboard": "b2b_portal",
  "brand-tap-analytics": "nfc_brand_tap",
  "dev-tools": "dev_badges_admin",
  // monetization_live gates any future Revenue Command panel; no current panel mapped yet.
};

// Build a panel-id → definition lookup combining all PANEL_DEFS + synthetic merged entries.
const PANEL_LOOKUP = (() => {
  const map = {};
  Object.values(PANEL_DEFS).forEach(list => list.forEach(p => { map[p.id] = p; }));
  // Synthetic merged entries
  map["pipeline-merged"] = { id: "pipeline-merged", title: "Pipelines", icon: Activity, color: "bg-sky-100 text-sky-600" };
  map["data-quality-merged"] = { id: "data-quality-merged", title: "Data Quality", icon: Database, color: "bg-teal-100 text-teal-600" };
  map["acquisition-merged"] = { id: "acquisition-merged", title: "Acquisition", icon: Compass, color: "bg-indigo-100 text-indigo-600" };
  map["address-verification-merged"] = { id: "address-verification-merged", title: "Address Verification", icon: MapPin, color: "bg-teal-100 text-teal-600" };
  return map;
})();

// Priority rank for status dots — lower = higher priority (floats to top).
const STATUS_PRIORITY = { red: 0, orange: 1, yellow: 2, blue: 3, purple: 4, green: 5, gray: 6, black: 7 };
const STATUS_HEX = {
  red: "bg-red-500", orange: "bg-orange-500", yellow: "bg-amber-400",
  green: "bg-emerald-400", blue: "bg-sky-400", purple: "bg-purple-500",
  gray: "bg-slate-400", black: "bg-slate-800",
};
const shouldFloatToTop = (status) => status === "red" || status === "orange";

function sortPanelsAlpha(panelIds, getStatus) {
  // Red/orange float to top; rest alphabetical by title.
  return [...panelIds].sort((a, b) => {
    const pa = PANEL_LOOKUP[a];
    const pb = PANEL_LOOKUP[b];
    if (!pa) return 1;
    if (!pb) return -1;
    const sa = getStatus(pa.title);
    const sb = getStatus(pb.title);
    const aFloat = shouldFloatToTop(sa);
    const bFloat = shouldFloatToTop(sb);
    if (aFloat && !bFloat) return -1;
    if (bFloat && !aFloat) return 1;
    if (aFloat && bFloat) {
      // Both float — red before orange
      const pr = (STATUS_PRIORITY[sa] ?? 9) - (STATUS_PRIORITY[sb] ?? 9);
      if (pr !== 0) return pr;
    }
    return (pa.title || "").localeCompare(pb.title || "");
  });
}

function groupWorstStatus(panelIds, getStatus) {
  let worst = "gray";
  let worstRank = STATUS_PRIORITY.gray;
  for (const pid of panelIds) {
    const p = PANEL_LOOKUP[pid];
    if (!p) continue;
    const s = getStatus(p.title);
    const r = STATUS_PRIORITY[s] ?? 9;
    if (r < worstRank) { worst = s; worstRank = r; }
  }
  return worst;
}

// ── Pulse Strip ─────────────────────────────────────────────────
const PulseStrip = ({ metrics, onMetricClick }) => {
  if (!metrics?.length) return null;
  const dotColor = { red: "bg-red-500", yellow: "bg-amber-400", green: "bg-emerald-400", blue: "bg-sky-400" };
  const bgStyle = {
    red: "bg-red-50 border-red-200 hover:border-red-400",
    yellow: "bg-amber-50 border-amber-200 hover:border-amber-400",
    green: "bg-white border-slate-200 hover:border-slate-400",
    blue: "bg-sky-50 border-sky-200 hover:border-sky-400",
  };
  const valStyle = { red: "text-red-600", yellow: "text-amber-600", green: "text-slate-700", blue: "text-sky-600" };
  const lblStyle = { red: "text-red-500/80", yellow: "text-amber-600/70", green: "text-slate-500", blue: "text-sky-600/70" };
  const sparkColor = { red: "#ef4444", yellow: "#f59e0b", green: "#64748b", blue: "#0ea5e9" };
  // For "bad" metrics, going UP is bad (inverted trend arrow)
  const invertedKeys = new Set(["high_friction", "alerts", "price_reports", "stale_offers", "submissions", "bugs", "recheck"]);

  return (
    <div className="grid grid-cols-4 gap-2" data-testid="pulse-strip">
      {metrics.map(m => (
        <button
          key={m.key}
          onClick={() => onMetricClick(m)}
          className={`rounded-xl border px-2 py-2.5 text-center transition-all cursor-pointer ${bgStyle[m.status]}`}
          data-testid={`pulse-${m.key}`}
        >
          <div className="flex items-center justify-center gap-1.5 mb-0.5">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor[m.status]} ${m.status === "red" ? "animate-pulse" : ""}`} />
          </div>
          <p className={`text-xl font-black leading-none ${valStyle[m.status]}`}>{m.value}</p>
          <p className={`text-[11px] font-semibold mt-1 leading-tight ${lblStyle[m.status]}`}>{m.label}</p>
          {/* Sparkline + trend row */}
          <div className="flex items-center justify-center gap-1.5 mt-1.5">
            {m.sparkline && <Sparkline data={m.sparkline} color={sparkColor[m.status]} width={36} height={12} />}
            {m.trend && <TrendArrow trend={m.trend} change={m.trend_change} inverted={invertedKeys.has(m.key)} />}
          </div>
        </button>
      ))}
    </div>
  );
};

// ── Performance helper ───────────────────────────────────────────
// Returns "good" | "warn" | "bad" based on thresholds
// For "higher is better" metrics: perf(value, 80, 50) → good if >= 80, warn if >= 50, bad otherwise
// For "lower is better" metrics (inverted): perfInv(value, 5, 10) → good if <= 5, warn if <= 10, bad otherwise
const perf = (val, goodThresh, warnThresh) => {
  const v = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(v)) return undefined;
  return v >= goodThresh ? "good" : v >= warnThresh ? "warn" : "bad";
};
const perfInv = (val, goodThresh, warnThresh) => {
  const v = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(v)) return undefined;
  return v <= goodThresh ? "good" : v <= warnThresh ? "warn" : "bad";
};

// ── MPM helpers ──────────────────────────────────────────────────
const MPM_DOT = { balanced: "bg-green-500", supply_shortage: "bg-red-500", demand_shortage: "bg-blue-500", early: "bg-yellow-500" };

// ════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════
export default function AdminPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pulse, setPulse] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // v1.0.0 Batch B — 12-group state. Collapsed by default except first group.
  // Persisted in localStorage so it survives page refreshes.
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = localStorage.getItem("bdl_admin_open_groups");
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return { health_alerts: true, pipeline_queues: true };
  });
  const toggleGroup = (groupId) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [groupId]: !prev[groupId] };
      try { localStorage.setItem("bdl_admin_open_groups", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  // Danger Zone (group 12) requires an extra confirm tap before expanding.
  const [dangerUnlocked, setDangerUnlocked] = useState(false);

  // v1.0.8 Document 4 Step 5 — DecisionIssueDrawer state.
  // Set by DecisionIntelligenceGroup's onIssueClick callback (Panel 2 + 3 row taps).
  const [openIssueId, setOpenIssueId] = useState(null);
  const { flags: featureFlags } = useFeatureFlags();
  const isPanelFlagGated = (panelId) => {
    const flagKey = PANEL_FLAG_GATES[panelId];
    if (!flagKey) return false;
    return featureFlags[flagKey] !== true;
  };

  // Data states (carried from original)
  const [health, setHealth] = useState(null);
  const [userGrowth, setUserGrowth] = useState(null);
  const [supply, setSupply] = useState(null);
  const [demand, setDemand] = useState(null);
  const [redemptions, setRedemptions] = useState(null);
  const [verification, setVerification] = useState(null);
  const [aiDiscovery, setAiDiscovery] = useState(null);
  const [categories, setCategories] = useState(null);
  const [geo, setGeo] = useState(null);
  const [dataSets, setDataSets] = useState({});
  const [freshness, setFreshness] = useState(null);
  const [quickTestHidden, setQuickTestHidden] = useState(() => !!localStorage.getItem("hide_quick_test"));
  const [drillRegion, setDrillRegion] = useState(null);
  const [priceReports, setPriceReports] = useState([]);
  const [prStatusFilter, setPrStatusFilter] = useState("pending");
  const [prLoading, setPrLoading] = useState(false);
  const [pendingReportCount, setPendingReportCount] = useState(0);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [reVerification, setReVerification] = useState(null);
  const [rvLoading, setRvLoading] = useState(false);
  const [selectedRv, setSelectedRv] = useState(new Set());
  const [lastRefreshedPr, setLastRefreshedPr] = useState(null);

  const fetchPriceReports = useCallback(async (status) => {
    setPrLoading(true);
    try {
      const res = await axios.get(`${API}/admin/price-reports?status=${status}`);
      setPriceReports(res.data);
      setLastRefreshedPr(Date.now());
    } catch { setPriceReports([]); }
    finally { setPrLoading(false); }
  }, []);

  const handleReportAction = async (reportId, action) => {
    try {
      const res = await axios.put(`${API}/admin/price-reports/${reportId}`, { action });
      toast.success(res.data.message);
      fetchPriceReports(prStatusFilter);
      const countRes = await axios.get(`${API}/admin/price-reports?status=pending`);
      setPendingReportCount(countRes.data.length);
    } catch { toast.error("Failed to update report"); }
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await axios.post(`${API}/admin/price-reports/send-digest`);
      toast.success(res.data.message);
    } catch { toast.error("Failed to send digest"); }
    finally { setSendingDigest(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [pulseRes, h, ug, s, d, r, v, ai, cat, g, f] = await Promise.all([
          axios.get(`${API}/admin/pulse`),
          axios.get(`${API}/analytics/marketplace-health`),
          axios.get(`${API}/analytics/user-growth`),
          axios.get(`${API}/analytics/supply-health`),
          axios.get(`${API}/analytics/demand-activity`),
          axios.get(`${API}/analytics/redemption-intelligence`),
          axios.get(`${API}/analytics/verification-health`),
          axios.get(`${API}/analytics/ai-discovery`),
          axios.get(`${API}/analytics/category-performance`),
          axios.get(`${API}/analytics/geographic-coverage`),
          axios.get(`${API}/analytics/data-freshness`),
        ]);
        setPulse(pulseRes.data);
        setHealth(h.data); setUserGrowth(ug.data); setSupply(s.data); setDemand(d.data);
        setRedemptions(r.data); setVerification(v.data); setAiDiscovery(ai.data);
        setCategories(cat.data); setGeo(g.data); setFreshness(f.data);
        try {
          const prCount = await axios.get(`${API}/admin/price-reports?status=pending`);
          setPendingReportCount(prCount.data.length);
        } catch {}
      } catch (e) { console.error("Command center load error:", e); }
      finally { setLoading(false); }
    })();
  }, []);

  // v1.0.0 Batch C — fallback to "gray" (inactive) when a panel has no
  // explicit status from the pulse endpoint. Green is reserved for "healthy
  // with live data" — not for "we don't know yet".
  const getPanelStatus = (title) => pulse?.panel_statuses?.[title] || "gray";

  const scrollToPanel = (panelId) => {
    // Auto-open the group containing this panel before scrolling.
    const parentGroup = GROUPS.find((g) => g.panels.includes(panelId));
    if (parentGroup && !openGroups[parentGroup.id]) {
      setOpenGroups((prev) => ({ ...prev, [parentGroup.id]: true }));
    }
    // Small delay to let the group expand before scrolling.
    setTimeout(() => {
      const el = document.getElementById(`panel-${panelId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        const chevronDown = el.querySelector('[class*="lucide-chevron-down"]');
        if (!chevronDown) {
          const btn = el.querySelector("button");
          if (btn) setTimeout(() => btn.click(), 400);
        }
      }
    }, 250);
    setSidebarOpen(false);
  };

  const handlePulseClick = (metric) => {
    // Find which group this panel belongs to and open it before scrolling
    for (const group of GROUPS) {
      for (const pid of group.panels) {
        const p = PANEL_LOOKUP[pid];
        if (p && p.title === metric.panel) {
          setOpenGroups((prev) => ({ ...prev, [group.id]: true }));
          setTimeout(() => scrollToPanel(p.id), 100);
          return;
        }
      }
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;

  const filteredCities = drillRegion ? geo?.cities?.filter(c => c.region === drillRegion) : [];

  // ── Render panels by ID ─────────────────────────────────────
  const renderPanel = (panelDef) => {
    const { id, title, icon, color } = panelDef;
    const status = getPanelStatus(title);

    switch (id) {
      // ── COCKPIT ──
      case "pipeline":
        return (
          <div key={id} className="space-y-4">
            <SectionPanel id={`panel-${id}`} title={title} icon={icon} iconColor={color} defaultOpen={true} panelStatus={status}><WorkflowPipelinePanel /></SectionPanel>
            <SectionPanel id="panel-city-queue" title="City Priority Queue" icon={icon} iconColor="bg-violet-100 text-violet-600" panelStatus={null}><CityQueuePanel /></SectionPanel>
          </div>
        );
      case "friction":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} defaultOpen={true} panelStatus={status}><FrictionIndexPanel /></SectionPanel>;
      case "alerts":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} defaultOpen={true} panelStatus={status}><AlertsPanel /></SectionPanel>;
      case "price-reports":
        return (
          <div key={id} className="relative">
            <SectionPanel id={`panel-${id}`} title={title} icon={icon} iconColor={color} panelStatus={status} onOpen={() => { if (priceReports.length === 0) fetchPriceReports(prStatusFilter); }}>
              <PriceReportsContent
                priceReports={priceReports} prStatusFilter={prStatusFilter} setPrStatusFilter={setPrStatusFilter}
                fetchPriceReports={fetchPriceReports} prLoading={prLoading} handleReportAction={handleReportAction}
                handleSendDigest={handleSendDigest} sendingDigest={sendingDigest} pendingReportCount={pendingReportCount}
                lastRefreshedAt={lastRefreshedPr}
              />
            </SectionPanel>
            {pendingReportCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center z-10 animate-pulse">{pendingReportCount}</span>}
          </div>
        );
      case "data-completeness":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <DataCompletenessPanel />
          </SectionPanel>
        );
      case "reverification":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status} onOpen={async () => {
            if (!reVerification) {
              setRvLoading(true);
              try { const res = await axios.get(`${API}/admin/reverification-priority`); setReVerification(res.data); } catch {}
              finally { setRvLoading(false); }
            }
          }}>
            {rvLoading ? <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div> : reVerification && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <StatCard label="Total" value={reVerification.summary.total_records} />
                  <StatCard label="Critical" value={reVerification.summary.critical_count} color="text-red-600" performance={reVerification.summary.critical_count > 0 ? "bad" : "good"} />
                  <StatCard label="Moderate" value={reVerification.summary.moderate_count} color="text-amber-600" performance={reVerification.summary.moderate_count > 5 ? "warn" : "good"} />
                </div>
                {selectedRv.size > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-xl" data-testid="rv-batch-bar">
                    <span className="text-xs font-bold text-green-700">{selectedRv.size} selected</span>
                    <button onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/admin/batch/mark-verified`, { business_ids: [...selectedRv] });
                        const actionId = res.data.action_id;
                        toast.success(res.data.message, {
                          action: actionId ? { label: "Undo", onClick: async () => {
                            try { await axios.post(`${API}/admin/actions/${actionId}/undo`); toast.success("Undone"); setReVerification(null); setRvLoading(true); try { const r = await axios.get(`${API}/admin/reverification-priority`); setReVerification(r.data); } catch {} finally { setRvLoading(false); } } catch { toast.error("Undo window expired"); }
                          }} : undefined,
                          duration: 6000,
                        });
                        setSelectedRv(new Set()); setReVerification(null); setRvLoading(true);
                        try { const r = await axios.get(`${API}/admin/reverification-priority`); setReVerification(r.data); } catch {} finally { setRvLoading(false); }
                      } catch { toast.error("Batch verify failed"); }
                    }} className="px-3 py-1.5 rounded-lg bg-green-500 text-white text-[10px] font-bold hover:bg-green-600 flex items-center gap-1" data-testid="rv-batch-verify">
                      <Check className="w-3 h-3" /> Mark All Verified
                    </button>
                    <button onClick={() => setSelectedRv(new Set())} className="text-[10px] text-slate-400 hover:text-slate-600 ml-auto">Clear</button>
                  </div>
                )}
                <label className="flex items-center gap-2 px-1 cursor-pointer">
                  <input type="checkbox" checked={reVerification.records.length > 0 && selectedRv.size === Math.min(reVerification.records.length, 20)} onChange={(e) => { e.target.checked ? setSelectedRv(new Set(reVerification.records.slice(0, 20).map(r => r.business_id))) : setSelectedRv(new Set()); }} className="w-3.5 h-3.5 rounded accent-green-500" data-testid="rv-select-all" />
                  <span className="text-[10px] text-slate-400 font-medium">Select all</span>
                </label>
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {reVerification.records.slice(0, 20).map(r => (
                    <div key={r.business_id} className={`px-3 py-2 rounded-lg transition ${selectedRv.has(r.business_id) ? "bg-green-50 border border-green-200" : "bg-slate-50"}`}>
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={selectedRv.has(r.business_id)} onChange={(e) => { const s = new Set(selectedRv); e.target.checked ? s.add(r.business_id) : s.delete(r.business_id); setSelectedRv(s); }} className="w-3.5 h-3.5 rounded accent-green-500 flex-shrink-0" data-testid={`rv-check-${r.business_id}`} />
                        <span className={`text-xs font-bold w-8 ${r.priority_score >= 60 ? "text-red-600" : r.priority_score >= 30 ? "text-amber-600" : "text-green-600"}`}>{r.priority_score}</span>
                        <span className="text-xs text-slate-700 flex-1 truncate">{r.name}</span>
                        <span className="text-[10px] text-slate-400">${r.offer_value_est}</span>
                        <button onClick={async () => { try { const res = await axios.post(`${API}/admin/business/${r.business_id}/mark-verified`); const actionId = res.data.action_id; toast.success(`Marked ${r.name} verified`, { action: actionId ? { label: "Undo", onClick: async () => { try { await axios.post(`${API}/admin/actions/${actionId}/undo`); toast.success("Undone"); setReVerification(null); setRvLoading(true); try { const res2 = await axios.get(`${API}/admin/reverification-priority`); setReVerification(res2.data); } catch {} finally { setRvLoading(false); } } catch { toast.error("Undo window expired"); } }} : undefined, duration: 6000 }); setReVerification(null); setRvLoading(true); try { const res2 = await axios.get(`${API}/admin/reverification-priority`); setReVerification(res2.data); } catch {} finally { setRvLoading(false); } } catch { toast.error("Failed"); } }} className="px-2 py-1 rounded-lg bg-green-100 text-green-700 text-[10px] font-bold hover:bg-green-200 flex items-center gap-1 flex-shrink-0" data-testid={`verify-btn-${r.business_id}`}>
                          <Check className="w-3 h-3" /> Verify
                        </button>
                      </div>
                      {r.reasons.length > 0 && <p className="text-[10px] text-slate-400 mt-0.5 pl-10 truncate">{r.reasons.join(" · ")}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SectionPanel>
        );

      // ── OPS ──
      case "beta-testers":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <BetaTestersPanel />
          </SectionPanel>
        );
      case "monthly-challenge":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <MonthlyChallengePanel />
          </SectionPanel>
        );
      case "melissa-drip":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <MelissaDripPanel />
          </SectionPanel>
        );
      case "push-notifications":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <PushNotificationsPanel />
          </SectionPanel>
        );
      case "social-media":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <SocialMediaPanel />
          </SectionPanel>
        );
      case "copy-bank":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <CopyBankPanel />
          </SectionPanel>
        );
      case "pending-deletions":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <PendingDeletionsPanel />
          </SectionPanel>
        );
      case "staging-queue":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <StagingQueuePanel />
          </SectionPanel>
        );
      case "sweep-performance":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <SweepPerformancePanel />
          </SectionPanel>
        );
      case "pool-health":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <PoolHealthScorecardPanel />
          </SectionPanel>
        );
      case "workspace-inbox":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <InboxPanel />
          </SectionPanel>
        );
      case "quarantine-queue":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <QuarantineQueuePanel />
          </SectionPanel>
        );
      case "submissions-queue":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <SubmissionsPanel />
          </SectionPanel>
        );
      case "city-classification":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <CityClassificationPanel />
          </SectionPanel>
        );
      case "paid-sweep-queue":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <PaidSweepQueuePanel />
          </SectionPanel>
        );
      case "intent-signals":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <IntentSignalsPanel />
          </SectionPanel>
        );
      case "chain-consistency":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <ChainConsistencyPanel />
          </SectionPanel>
        );
      case "offer-intelligence":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <OfferIntelligencePanel />
          </SectionPanel>
        );
      case "batch-budget":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <BatchBudgetPanel />
          </SectionPanel>
        );
      case "tonights-spend":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <TonightsSpendPanel />
          </SectionPanel>
        );
      case "cost-tracker":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <CostTrackerPanel />
          </SectionPanel>
        );
      case "foundation-health":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <FoundationHealthPanel />
          </SectionPanel>
        );
      case "vendor-management":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <VendorManagementPanel />
          </SectionPanel>
        );
      case "vendor-analytics":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <VendorAnalyticsPanel />
          </SectionPanel>
        );
      case "portfolio-vendors":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <PortfolioVendorsPanel />
          </SectionPanel>
        );
      case "photo-review":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <PhotoReviewPanel />
          </SectionPanel>
        );
      case "vendor-outreach":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <VendorOutreachPanel />
          </SectionPanel>
        );
      case "b2b-dashboard":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <B2BDashboardPanel />
          </SectionPanel>
        );
      case "brand-contributions":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <BrandContributionsPanel />
          </SectionPanel>
        );
      case "brand-tap-analytics":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <BrandTapAnalyticsPanel />
          </SectionPanel>
        );
      case "dietary-suggestions":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <DietarySuggestionsPanel />
          </SectionPanel>
        );
      case "predictiq-events":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <PredictHQPanel />
          </SectionPanel>
        );
      case "food-holidays":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <FoodHolidayPanel />
          </SectionPanel>
        );
      case "tips-management":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <TipsManagementPanel />
          </SectionPanel>
        );
      case "age-restrictions":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <AgeRestrictionsPanel />
          </SectionPanel>
        );
      case "category-intelligence":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <CategoryIntelligencePanel />
          </SectionPanel>
        );
      case "outreach-infrastructure":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <OutreachInfrastructurePanel />
          </SectionPanel>
        );
      case "rolling-pipeline-status":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <RollingPipelineStatusPanel />
          </SectionPanel>
        );
      case "coverage-map":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <CoverageMapPanel />
          </SectionPanel>
        );
      case "source-roi":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <SourceROIPanel />
          </SectionPanel>
        );
      case "city-management":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <CityManagementPanel />
          </SectionPanel>
        );
      case "admin-offers":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <AdminOffersPanel />
          </SectionPanel>
        );
      case "referral-intel":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status} onOpen={async () => {
            try {
              const res = await axios.get(`${API}/admin/referral-stats`);
              setDataSets(prev => ({ ...prev, referralStats: res.data }));
            } catch {}
          }}>
            <ReferralIntelPanel data={dataSets?.referralStats} />
          </SectionPanel>
        );
      case "marketplace":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} defaultOpen={true} panelStatus={status}>
            {health && (
              <div>
                <div className="text-center mb-4">
                  <p className="text-xs text-slate-400 uppercase tracking-wider">North Star</p>
                  <p className="text-4xl font-black text-amber-500 mt-1">{health?.north_star?.verified_deals_redeemed || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">Verified Deals Redeemed</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <StatCard label="Redemption Rate" value={`${health?.supporting?.deal_redemption_rate || 0}%`} color="text-emerald-600" performance={perf(health?.supporting?.deal_redemption_rate, 20, 5)} />
                  <StatCard label="Active Users (7d)" value={health?.supporting?.daily_active_users || 0} color="text-sky-600" performance={perf(health?.supporting?.daily_active_users, 10, 1)} />
                  <StatCard label="Active Deals" value={health?.supporting?.businesses_with_active_deals || 0} color="text-emerald-600" performance={perf(health?.supporting?.businesses_with_active_deals, 50, 10)} />
                  <StatCard label="Liquidity" value={health?.supporting?.users_per_business || 0} color="text-purple-600" performance={perf(health?.supporting?.users_per_business, 3, 1)} />
                </div>
              </div>
            )}
          </SectionPanel>
        );
      case "geographic":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}><GeographicDemandPanel /></SectionPanel>;
      case "performance":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}><OfferPerformancePanel /></SectionPanel>;
      case "velocity":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}><DealVelocityPanel /></SectionPanel>;
      case "funnel":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}><ConversionFunnelPanel /></SectionPanel>;
      case "retention":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}><RetentionDashboardPanel /></SectionPanel>;
      case "category-demand":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}><CategoryDemandPanel /></SectionPanel>;
      case "user-growth":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            {userGrowth && (
              <div className="grid grid-cols-2 gap-3">
                <StatCard label="Total Users" value={userGrowth.total_users} color="text-sky-600" />
                <StatCard label="New Today" value={userGrowth.new_today} color="text-green-600" performance={perf(userGrowth.new_today, 3, 1)} />
                <StatCard label="New This Week" value={userGrowth.new_this_week} color="text-sky-600" performance={perf(userGrowth.new_this_week, 10, 3)} />
                <StatCard label="New This Month" value={userGrowth.new_this_month} color="text-sky-600" performance={perf(userGrowth.new_this_month, 30, 10)} />
                <StatCard label="DAU" value={userGrowth.daily_active_users} sub="Daily active" color="text-indigo-600" performance={perf(userGrowth.daily_active_users, 20, 5)} />
                <StatCard label="MAU" value={userGrowth.monthly_active_users} sub="Monthly active" color="text-indigo-600" performance={perf(userGrowth.monthly_active_users, 100, 20)} />
                <StatCard label="Retention Rate" value={`${userGrowth.user_retention_rate}%`} sub="Week over week" color="text-amber-600" performance={perf(userGrowth.user_retention_rate, 50, 20)} />
                <StatCard label="Birthdays This Week" value={userGrowth.birthday_users_this_week} color="text-pink-600" />
              </div>
            )}
          </SectionPanel>
        );
      case "supply":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            {supply && (
              <div className="grid grid-cols-2 gap-3">
                <StatCard label="Total Businesses" value={supply.total_businesses} color="text-emerald-600" />
                <StatCard label="Verified (50%+)" value={supply.verified_businesses} color="text-green-600" performance={perf(supply.total_businesses > 0 ? (supply.verified_businesses / supply.total_businesses * 100) : 0, 50, 20)} />
                <StatCard label="Active Deals" value={supply.businesses_with_active_deals} color="text-emerald-600" performance={perf(supply.businesses_with_active_deals, 50, 10)} />
                <StatCard label="Pending Submissions" value={supply.pending_verifications} color="text-amber-600" performance={perfInv(supply.pending_verifications, 2, 10)} />
                <StatCard label="A.I. Discovered" value={supply.ai_discovered} color="text-purple-600" />
                <StatCard label="Growth (Month)" value={`+${supply.supply_growth_this_month}`} color="text-green-600" performance={perf(supply.supply_growth_this_month, 5, 1)} />
              </div>
            )}
          </SectionPanel>
        );
      case "demand":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            {demand && (
              <div className="grid grid-cols-2 gap-3">
                <StatCard label="Deal Views" value={demand.total_deal_views} color="text-orange-600" performance={perf(demand.total_deal_views, 100, 20)} />
                <StatCard label="Searches" value={demand.total_searches} color="text-orange-600" performance={perf(demand.total_searches, 50, 10)} />
                <StatCard label="Favorites" value={demand.total_favorites} color="text-red-500" performance={perf(demand.total_favorites, 20, 5)} />
                <StatCard label="Tracker Signups" value={demand.tracker_signups} color="text-sky-600" />
                <StatCard label="Tracker Claimed" value={demand.tracker_claimed} color="text-green-600" performance={perf(demand.tracker_claimed, 10, 1)} />
                <StatCard label="Total Redemptions" value={demand.total_redemptions} color="text-emerald-600" performance={perf(demand.total_redemptions, 20, 5)} />
              </div>
            )}
          </SectionPanel>
        );
      case "redemptions":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            {redemptions && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <StatCard label="Today" value={redemptions.redeemed_today} color="text-amber-600" />
                  <StatCard label="This Week" value={redemptions.redeemed_this_week} color="text-amber-600" />
                  <StatCard label="This Month" value={redemptions.redeemed_this_month} color="text-amber-600" />
                </div>
                {redemptions.top_redeemed_businesses?.length > 0 && (
                  <div><p className="text-xs font-medium text-slate-500 mb-2">Top Businesses</p><div className="space-y-1.5">{redemptions.top_redeemed_businesses.map((b, i) => (
                    <div key={i} className="flex items-center justify-between text-sm"><span className="text-slate-700 truncate">{b.name}</span><span className="font-semibold text-amber-600">{b.count}</span></div>
                  ))}</div></div>
                )}
              </div>
            )}
          </SectionPanel>
        );
      case "category-perf":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            {categories && (
              <div className="space-y-2">
                <div className="grid grid-cols-5 text-xs font-medium text-slate-400 px-1"><span>Category</span><span className="text-right">Biz</span><span className="text-right">Avg $</span><span className="text-right">Ease</span><span className="text-right">Fav</span></div>
                {categories.categories?.slice(0, 15).map((c, i) => (
                  <div key={i} className="grid grid-cols-5 text-sm items-center py-1.5 px-1 border-b border-slate-50 last:border-0">
                    <span className="text-slate-700 font-medium truncate text-xs">{c.name}</span>
                    <span className="text-right font-semibold text-slate-800">{c.count}</span>
                    <span className="text-right text-green-600">${c.avg_value}</span>
                    <span className="text-right text-amber-600">{c.avg_ease}</span>
                    <span className="text-right text-red-400">{c.favorites}</span>
                  </div>
                ))}
              </div>
            )}
          </SectionPanel>
        );

      // ── TOOLS ──
      case "filter-usage":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}><FilterAnalyticsPanel /></SectionPanel>;
      case "login-activity":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}><LoginActivityPanel /></SectionPanel>;
      case "stickiness":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}><OfferStickinessPanel /></SectionPanel>;
      case "bug-bounty":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}><BugBountyPanel /></SectionPanel>;
      case "duplicates":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}><DuplicateCheckPanel /></SectionPanel>;
      case "discovery":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}><DiscoveryAnalyticsPanel /></SectionPanel>;
      case "sessions":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}><SessionInsightsPanel /></SectionPanel>;
      case "offer-intel":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}><OfferIntelPanel /></SectionPanel>;
      case "search":
        return <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}><SearchAnalyticsPanel /></SectionPanel>;
      case "verification":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            {verification && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <StatCard label="Total Verifications" value={verification.total_verifications} color="text-green-600" />
                  <StatCard label="Accuracy Rate" value={`${verification.accuracy_rate}%`} color="text-emerald-600" performance={perf(verification.accuracy_rate, 80, 50)} />
                  <StatCard label="Confirmed" value={verification.confirmed} color="text-green-600" performance="good" />
                  <StatCard label="Denied" value={verification.denied} color="text-red-600" performance={perfInv(verification.denied, 2, 10)} />
                  <StatCard label="Needs Re-verify" value={verification.needs_reverification} sub="90+ days stale" color="text-orange-600" performance={perfInv(verification.needs_reverification, 5, 20)} />
                </div>
                {verification.top_verifiers?.length > 0 && (
                  <div><p className="text-xs font-medium text-slate-500 mb-2">Top Verifiers</p><div className="space-y-1.5">{verification.top_verifiers.map((v, i) => (
                    <div key={i} className="flex items-center justify-between text-sm"><span className="text-slate-700">{v.name}</span><span className="font-medium text-amber-600">{v.count} verifs</span></div>
                  ))}</div></div>
                )}
              </div>
            )}
          </SectionPanel>
        );
      case "ai-discovery":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            {aiDiscovery && (
              <div className="space-y-4">
                {aiDiscovery.status === "foundation_ready" && (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-3"><p className="text-xs text-purple-700 font-medium">Foundation Ready</p><p className="text-xs text-purple-500 mt-1">Metrics will populate as the A.I. agent submits businesses.</p></div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <StatCard label="A.I. Discovered" value={aiDiscovery.total_ai_discovered} color="text-purple-600" />
                  <StatCard label="Confirmed by Users" value={aiDiscovery.ai_confirmed_by_users} color="text-green-600" performance={perf(aiDiscovery.ai_confirmed_by_users, 5, 1)} />
                  <StatCard label="Accuracy Rate" value={`${aiDiscovery.ai_accuracy_rate}%`} color="text-emerald-600" performance={perf(aiDiscovery.ai_accuracy_rate, 80, 50)} />
                  <StatCard label="False Positives" value={aiDiscovery.ai_false_positives} color="text-red-600" performance={perfInv(aiDiscovery.ai_false_positives, 2, 5)} />
                </div>
              </div>
            )}
          </SectionPanel>
        );
      case "geo-mpm":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            {geo && (
              <div className="space-y-4">
                <div><p className="text-xs font-medium text-slate-500 mb-1">{geo.total_cities_covered} cities covered</p></div>
                <div className="space-y-1.5">
                  {geo.regions?.map((r, i) => (
                    <button key={i} onClick={() => setDrillRegion(drillRegion === r.region ? null : r.region)} className={`w-full flex items-center gap-2 p-2.5 rounded-lg border text-left transition-colors text-sm ${drillRegion === r.region ? "bg-sky-50 border-sky-300" : "bg-white border-slate-100 hover:border-slate-300"}`}>
                      <span className={`w-3 h-3 rounded-full flex-shrink-0 ${MPM_DOT[r.status]}`} />
                      <span className="flex-1 font-medium text-slate-700 truncate">{r.region}</span>
                      <span className="text-xs text-slate-500">{r.businesses} biz</span>
                      <span className="text-xs text-slate-500">{r.users} users</span>
                    </button>
                  ))}
                </div>
                <AnimatePresence>
                  {drillRegion && filteredCities.length > 0 && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                        <p className="text-xs font-semibold text-slate-600 mb-2">{drillRegion} — Cities</p>
                        <div className="space-y-1 max-h-60 overflow-y-auto">
                          {filteredCities.map((c, i) => (
                            <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded-lg bg-white border border-slate-100 text-xs">
                              <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${MPM_DOT[c.status]}`} />
                              <span className="flex-1 font-medium text-slate-700">{c.city}</span>
                              <span className="text-slate-500">{c.businesses} biz</span>
                              <span className="text-slate-400">${c.avg_value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </SectionPanel>
        );
      case "freshness":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            {freshness && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <StatCard label="Total" value={freshness.total_businesses} color="text-slate-700" />
                  <StatCard label="Verified This Month" value={freshness.verified_this_month} color="text-green-600" />
                  <StatCard label="Stale Listings" value={freshness.stale_listings} color="text-orange-600" />
                  <StatCard label="Expired Deals" value={freshness.expired_deals} color="text-red-600" />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 bg-emerald-50 rounded-lg p-2 text-center border border-emerald-200"><p className="text-lg font-bold text-emerald-600">{freshness.confidence_distribution?.high || 0}</p><p className="text-[10px] text-emerald-500">High</p></div>
                  <div className="flex-1 bg-amber-50 rounded-lg p-2 text-center border border-amber-200"><p className="text-lg font-bold text-amber-600">{freshness.confidence_distribution?.medium || 0}</p><p className="text-[10px] text-amber-500">Medium</p></div>
                  <div className="flex-1 bg-slate-50 rounded-lg p-2 text-center border border-slate-200"><p className="text-lg font-bold text-slate-600">{freshness.confidence_distribution?.low || 0}</p><p className="text-[10px] text-slate-500">Low</p></div>
                </div>
              </div>
            )}
          </SectionPanel>
        );
      case "playbooks":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <PlaybooksPanel />
          </SectionPanel>
        );
      case "geo-drilldown":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <GeoDrilldownPanel />
          </SectionPanel>
        );
      case "rate-limits":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <RateLimitPanel />
          </SectionPanel>
        );
      case "dev-tools":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <div className="space-y-2">
              <button onClick={() => navigate("/concepts")} className="w-full flex items-center gap-3 px-4 py-3 bg-purple-50 border border-purple-200 rounded-xl hover:bg-purple-100 transition">
                <Sparkles className="w-4 h-4 text-purple-500" /><div className="flex-1 text-left"><p className="text-sm font-semibold text-purple-800">Themes & Layouts</p></div><ChevronRight className="w-4 h-4 text-purple-300" />
              </button>
              <button onClick={() => navigate("/onboarding-demo")} className="w-full flex items-center gap-3 px-4 py-3 bg-cyan-50 border border-cyan-200 rounded-xl hover:bg-cyan-100 transition">
                <Wand2 className="w-4 h-4 text-cyan-500" /><div className="flex-1 text-left"><p className="text-sm font-semibold text-cyan-800">Onboarding Styles</p></div><ChevronRight className="w-4 h-4 text-cyan-300" />
              </button>
              <div className="flex items-center justify-between px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-center gap-3"><Zap className="w-4 h-4 text-amber-500" /><div><p className="text-sm font-semibold text-amber-800">Quick Test Mode</p><p className="text-[10px] text-amber-500">{quickTestHidden ? "Hidden" : "Visible"}</p></div></div>
                <button onClick={() => { if (quickTestHidden) { localStorage.removeItem("hide_quick_test"); setQuickTestHidden(false); } else { localStorage.setItem("hide_quick_test", "1"); setQuickTestHidden(true); } }} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${quickTestHidden ? "bg-amber-500 text-white" : "bg-white text-amber-700 border border-amber-300"}`}>{quickTestHidden ? "Show" : "Hide"}</button>
              </div>
            </div>
          </SectionPanel>
        );
      case "source-health":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <SourceHealthPanel />
          </SectionPanel>
        );
      // ── v1.0.0 Batch B — Merged panels ──
      case "pipeline-merged":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <PipelinesMergedPanel
              WorkflowPipelinePanel={WorkflowPipelinePanel}
              RollingPipelineStatusPanel={RollingPipelineStatusPanel}
              DiscoveryAnalyticsPanel={DiscoveryAnalyticsPanel}
            />
          </SectionPanel>
        );
      case "data-quality-merged":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <DataQualityMergedPanel DataCompletenessPanel={DataCompletenessPanel} />
          </SectionPanel>
        );
      case "acquisition-merged":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <AcquisitionMergedPanel SourceROIPanel={SourceROIPanel} />
          </SectionPanel>
        );
      case "address-verification-merged":
        return (
          <SectionPanel id={`panel-${id}`} key={id} title={title} icon={icon} iconColor={color} panelStatus={status}>
            <AddressVerificationMergedPanel MelissaDripPanel={MelissaDripPanel} />
          </SectionPanel>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      {/* ── HEADER ─────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-slate-900 text-white px-4 py-3 flex items-center gap-3 shadow-lg">
        <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 transition" data-testid="back-button">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h1 className="text-base font-bold tracking-tight">Command Center</h1>
          <p className="text-[10px] text-slate-400">{pulse?.totals?.users || 0} users &middot; {pulse?.totals?.businesses || 0} businesses</p>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="lg:hidden w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 transition"
          data-testid="sidebar-toggle"
        >
          {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </button>
      </header>

      <div className="flex">
        {/* ── v1.0.0 Batch B — 12-Group Sidebar ── */}
        <aside className={`
          fixed lg:sticky top-[52px] left-0 z-40 h-[calc(100vh-52px)] w-64 bg-white border-r border-slate-200 overflow-y-auto
          transition-transform duration-200
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `} data-testid="sidebar-nav">
          <nav className="py-3">
            {GROUPS.map((group, idx) => {
              const visiblePanels = group.panels.filter((pid) => !isPanelFlagGated(pid));
              const sortedPanels = sortPanelsAlpha(visiblePanels, getPanelStatus);
              const worst = groupWorstStatus(sortedPanels, getPanelStatus);
              const isOpen = !!openGroups[group.id];
              const isDanger = group.dangerZone;
              return (
                <div key={group.id} className="mb-0.5">
                  <button
                    onClick={() => {
                      if (isDanger && !dangerUnlocked && !isOpen) {
                        if (window.confirm("Expand Danger Zone? This reveals system-level flags and controls.")) {
                          setDangerUnlocked(true);
                          toggleGroup(group.id);
                        }
                        return;
                      }
                      toggleGroup(group.id);
                    }}
                    className={`w-full px-4 py-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider transition ${
                      isOpen ? "text-sky-700 bg-sky-50" : isDanger ? "text-red-500 hover:bg-red-50" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
                    }`}
                    data-testid={`sidebar-group-${group.id}`}
                  >
                    <span className="text-[9px] font-bold text-slate-300 w-4">{idx + 1}</span>
                    {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <span className="flex-1 text-left truncate">{group.label}</span>
                    {worst !== "gray" && worst !== "green" && (
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_HEX[worst] || STATUS_HEX.gray} ${worst === "red" ? "animate-pulse" : ""}`} />
                    )}
                    {isDanger && <Shield className="w-3 h-3 flex-shrink-0" />}
                  </button>
                  {isOpen && (
                    <div className="ml-3 border-l border-slate-100 mb-1">
                      {sortedPanels.length === 0 ? (
                        <p className="px-4 py-1.5 text-[10px] text-slate-300 italic">
                          {isDanger ? "System flags managed via /api/admin/feature-flags" : "no panels"}
                        </p>
                      ) : sortedPanels.map((pid) => {
                        const p = PANEL_LOOKUP[pid];
                        if (!p) return null;
                        const pStatus = getPanelStatus(p.title);
                        return (
                          <button
                            key={pid}
                            onClick={() => { scrollToPanel(pid); }}
                            className="w-full px-4 py-1.5 flex items-center gap-2 text-[11px] text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition text-left"
                            data-testid={`sidebar-link-${pid}`}
                          >
                            {pStatus !== "green" && pStatus !== "gray" && (
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_HEX[pStatus] || STATUS_HEX.gray} ${pStatus === "red" ? "animate-pulse" : ""}`} />
                            )}
                            <span className="truncate">{p.title}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="mt-3 pt-3 border-t border-slate-100 px-3">
              <ActivityFeedPanel compact />
            </div>
          </nav>
        </aside>

        {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

        {/* ── MAIN CONTENT ── */}
        <main className="flex-1 min-w-0 px-4 py-4 space-y-4 lg:max-w-4xl" data-testid="command-center-content">
          <SystemHealthPanel />
          {/* v1.0.0 Batch C — North Star summary landing */}
          <NorthStarPanel
            onGroupClick={(groupLabel) => {
              const g = GROUPS.find((x) => x.label === groupLabel);
              if (!g) return;
              setOpenGroups((prev) => ({ ...prev, [g.id]: true }));
              setTimeout(() => {
                document.querySelector(`[data-testid="group-${g.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 200);
            }}
          />
          <LegendCard />
          <PulseStrip metrics={pulse?.metrics} onMetricClick={handlePulseClick} />

          {/* 12 Groups */}
          <div className="space-y-3" data-testid="admin-groups-container">
            {GROUPS.map((group, idx) => {
              const visiblePanels = group.panels.filter((pid) => !isPanelFlagGated(pid));
              const sortedPanels = sortPanelsAlpha(visiblePanels, getPanelStatus);
              const worst = groupWorstStatus(sortedPanels, getPanelStatus);
              const isOpen = !!openGroups[group.id];
              const isDanger = group.dangerZone;
              return (
                <div
                  key={group.id}
                  className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${
                    isDanger ? "border-red-200" : "border-slate-200"
                  }`}
                  data-testid={`group-${group.id}`}
                >
                  <button
                    onClick={() => {
                      if (isDanger && !dangerUnlocked && !isOpen) {
                        if (window.confirm("Expand Danger Zone? This reveals system-level flags and controls.")) {
                          setDangerUnlocked(true);
                          toggleGroup(group.id);
                        }
                        return;
                      }
                      toggleGroup(group.id);
                    }}
                    className={`w-full px-5 py-3.5 flex items-center gap-3 transition ${
                      isDanger ? "hover:bg-red-50" : "hover:bg-slate-50"
                    }`}
                    data-testid={`group-header-${group.id}`}
                  >
                    <span className="text-[10px] font-black text-slate-300 w-5">{String(idx + 1).padStart(2, "0")}</span>
                    <span className={`flex-1 text-left text-sm font-bold ${isDanger ? "text-red-600" : "text-slate-800"}`}>
                      {group.label}
                      {isDanger && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-black uppercase">Danger Zone</span>}
                    </span>
                    <span className="text-[10px] text-slate-400 font-medium">{sortedPanels.length} {sortedPanels.length === 1 ? "panel" : "panels"}</span>
                    {worst !== "gray" && worst !== "green" && (
                      <span className={`w-2 h-2 rounded-full ${STATUS_HEX[worst] || STATUS_HEX.gray} ${worst === "red" ? "animate-pulse" : ""}`} data-testid={`group-dot-${group.id}`} />
                    )}
                    {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </button>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 pt-1 space-y-3">
                          {group.id === "pipeline_queues" && (
                            <>
                              <LiveCityNightlyTile />
                              <div
                                className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-sky-50 border border-sky-200"
                                data-testid="digest-preview-action-row"
                              >
                              <button
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem("token");
                                    const res = await fetch(`${API}/admin/digest-preview`, {
                                      headers: { Authorization: `Bearer ${token}` },
                                    });
                                    if (!res.ok) {
                                      alert(`Preview failed: HTTP ${res.status}`);
                                      return;
                                    }
                                    const html = await res.text();
                                    const w = window.open("", "_blank");
                                    if (!w) {
                                      alert("Popup blocked. Allow popups for this site.");
                                      return;
                                    }
                                    w.document.open();
                                    w.document.write(html);
                                    w.document.close();
                                    w.document.title = "Morning Digest Preview";
                                  } catch (e) {
                                    alert(`Preview error: ${e.message}`);
                                  }
                                }}
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold transition shadow-sm"
                                data-testid="preview-digest-button"
                              >
                                <Mail className="w-3.5 h-3.5" />
                                Preview Digest
                              </button>
                              <span className="text-[11px] text-sky-800 leading-snug" data-testid="preview-digest-note">
                                Preview shows today's data. Refresh anytime. Does not affect tonight's real send.
                              </span>
                            </div>
                            </>
                          )}
                          {group.id === "decision_intelligence" && (
                            <>
                              <DecisionIntelligenceGroup
                                onIssueClick={(issueId) => {
                                  if (issueId) setOpenIssueId(issueId);
                                }}
                              />
                              <div className="mt-4">
                                <DecisionIntelligenceSummaryTile />
                              </div>
                            </>
                          )}
                          {group.id === "decision_intelligence" ? null : sortedPanels.length === 0 ? (
                            <p className="text-xs text-slate-400 italic text-center py-4">
                              {isDanger ? "System flags are managed via /api/admin/feature-flags endpoints. No in-dashboard UI at v1.0." : "No panels in this group."}
                            </p>
                          ) : sortedPanels.map((pid) => {
                            const p = PANEL_LOOKUP[pid];
                            if (!p) return null;
                            return <div key={pid}>{renderPanel(p)}</div>;
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </main>
      </div>

      {/* v1.0.8 Document 4 Step 5 — Decision Issue Drawer */}
      <DecisionIssueDrawer
        issueId={openIssueId}
        open={!!openIssueId}
        onOpenChange={(o) => { if (!o) setOpenIssueId(null); }}
        onResolved={() => {
          // After resolving, the dashboard recomputes itself on the next 30s poll.
          // Force-close the drawer (already happens) — nothing else to do here.
        }}
      />
    </div>
  );
}

// ── Price Reports Sub-Component ─────────────────────────────────
function PriceReportsContent({ priceReports, prStatusFilter, setPrStatusFilter, fetchPriceReports, prLoading, handleReportAction, handleSendDigest, sendingDigest, pendingReportCount, lastRefreshedAt }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-end -mb-2"><PanelRefreshButton onRefresh={() => fetchPriceReports(prStatusFilter)} lastRefreshedAt={lastRefreshedAt} testId="price-reports-refresh" /></div>
      <div className="flex gap-2" data-testid="pr-status-tabs">
        {["pending", "applied", "dismissed", "all"].map(st => (
          <button key={st} onClick={() => { setPrStatusFilter(st); fetchPriceReports(st); }} className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition ${prStatusFilter === st ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`pr-tab-${st}`}>
            {st}{st === "pending" && pendingReportCount > 0 ? ` (${pendingReportCount})` : ""}
          </button>
        ))}
      </div>
      <button onClick={handleSendDigest} disabled={sendingDigest || pendingReportCount === 0} className="flex items-center gap-2 px-4 py-2.5 bg-sky-50 border border-sky-200 rounded-xl hover:bg-sky-100 transition text-sm font-medium text-sky-700 disabled:opacity-50" data-testid="pr-send-digest">
        {sendingDigest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send Email Digest
      </button>
      {prLoading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : priceReports.length === 0 ? (
        <div className="text-center py-6 text-sm text-slate-400" data-testid="pr-empty">No {prStatusFilter} reports</div>
      ) : (
        <div className="space-y-3" data-testid="pr-list">
          {priceReports.map(r => {
            const isPending = r.admin_status === "pending";
            return (
              <div key={r.id} className={`border rounded-xl p-4 space-y-2 ${r.is_mismatch ? "border-red-200 bg-red-50/50" : "border-slate-200 bg-white"}`} data-testid={`pr-card-${r.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-800">{r.business_name}</p>
                    <p className="text-[10px] text-slate-400">{r.user_name} &middot; {r.created_at?.slice(0, 10)}</p>
                  </div>
                  {r.is_mismatch && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold"><AlertTriangle className="w-3 h-3" /> Mismatch</span>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-white/80 rounded-lg p-2 border border-slate-100"><p className="text-[10px] text-slate-400">Listed</p><p className="text-sm font-bold text-slate-700">${(r.listed_value || 0).toFixed(2)}</p></div>
                  <div className={`rounded-lg p-2 border ${r.is_mismatch ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}><p className="text-[10px] text-slate-400">Actual</p><p className={`text-sm font-bold ${r.is_mismatch ? "text-red-600" : "text-green-600"}`}>${(r.actual_value || 0).toFixed(2)}</p></div>
                </div>
                {r.what_received && <p className="text-xs text-slate-600"><span className="text-slate-400">Received:</span> {r.what_received}</p>}
                {isPending && (
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => handleReportAction(r.id, "apply")} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-green-500 text-white text-xs font-bold hover:bg-green-600 transition" data-testid={`pr-apply-${r.id}`}><CheckCircle className="w-3.5 h-3.5" /> Apply</button>
                    <button onClick={() => handleReportAction(r.id, "dismiss")} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200 transition" data-testid={`pr-dismiss-${r.id}`}><XCircle className="w-3.5 h-3.5" /> Dismiss</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
